import { Delete, DragIndicator, PlaylistAdd, Save } from "@mui/icons-material";
import {
	Alert,
	Checkbox,
	Chip,
	IconButton,
	Paper,
	Snackbar,
	Tooltip,
} from "@mui/material";
import {
	DataGrid,
	type GridCellParams,
	type GridColDef,
	type GridRenderCellParams,
} from "@mui/x-data-grid";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isNil } from "lodash-es";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import {
	type MenuOptionType,
	useContextMenuMESF,
} from "../../../../../controls";
import { ColorPickerV2 } from "../components/ColorPickerV2";
import { TagsTreeModalV2 } from "../components/TagsTreeModalV2";
import { BitSelectorModal } from "../components/modals/BitSelectorModal";
import { LoadViewModalV2 } from "../components/modals/LoadViewModalV2";
import { SaveUpdateDeleteViewModalV2 } from "../components/modals/SaveUpdateDeleteViewModalV2";
import { useTrendingContextV2 } from "../context/TrendingContextV2";
import type { TagTableRow, ViewTagsMap } from "../models/TrendingModelsV2";
import {
	deleteAllViewTagsFromView,
	saveTagDefaults,
	upsertView,
	upsertViewTag,
} from "../repository/TrendingRepositoryV2";
import { saveViewToLocal } from "../utils/LocalStorageUtils";
import { getRandomColor } from "../utils/tagColors";

const formatDeltaTime = (ms: number) => {
	if (!Number.isFinite(ms) || ms <= 0) return "";
	if (ms < 1000) return `${Math.round(ms)}ms`;

	const totalSeconds = Math.floor(ms / 1000);
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;

	if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
	if (minutes > 0) return `${minutes}m ${seconds}s`;
	return `${seconds}s`;
};

export const TagsTableV2: React.FC = () => {
	const queryClient = useQueryClient();
	const { registerConfig, showContextMenu } = useContextMenuMESF();

	const {
		state: {
			viewTags,
			cursorData,
			viewSelected,
			views,
			seriesMinMax,
			seriesPointCounts,
		},
		actions: {
			setViewTags,
			setViewTagsAndRefetch,
			setViewSelected,
			setViews,
			highlightSeries,
		},
	} = useTrendingContextV2();

	// Number formatters for consistent thousand separator and decimal formatting
	const numberFormatterOneDecimal = useMemo(
		() =>
			new Intl.NumberFormat("en-US", {
				minimumFractionDigits: 1,
				maximumFractionDigits: 1,
			}),
		[],
	);

	const numberFormatterNoDecimal = useMemo(
		() =>
			new Intl.NumberFormat("en-US", {
				minimumFractionDigits: 0,
				maximumFractionDigits: 0,
			}),
		[],
	);

	// State management
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [selectedRowTagId, setSelectedRowTagId] = useState<number | null>(null);
	const [tagsTreeModalOpen, setTagsTreeModalOpen] = useState(false);
	const [bitSelectorModalOpen, setBitSelectorModalOpen] = useState(false);
	const [saveAsViewModalOpen, setSaveAsViewModalOpen] = useState(false);
	const [loadViewOpen, setLoadViewOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [draggedRowId, setDraggedRowId] = useState<number | null>(null);
	const [dragOverRowId, setDragOverRowId] = useState<number | null>(null);

	// Mutations
	const deleteAllViewTags = useMutation({
		mutationFn: (viewId: number) => deleteAllViewTagsFromView(viewId),
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const updateView = useMutation({
		mutationFn: ({
			viewId,
			viewName,
			isPublic,
		}: {
			viewId: number | null;
			viewName: string;
			isPublic: boolean;
		}) => upsertView({ viewId, viewName, isPublic }),
		onSuccess: async (viewId) => {
			// Save complete state to local storage (metadata + virtual tags)
			if (viewSelected?.ViewId) {
				saveViewToLocal(viewSelected.ViewId, viewTags);
			}

			// Wait for delete to finish before inserting new tags
			await deleteAllViewTags.mutateAsync(viewSelected?.ViewId ?? 0);

			// Filter out bit-extracted tags for backend persistence
			const backendTags = Object.values(viewTags).filter(
				({ viewTag }) => !viewTag.IsBitExtracted,
			);

			const results = await Promise.allSettled(
				backendTags
					.sort((a, b) => b.order - a.order)
					.map(({ viewTag, order }) =>
						updateViewTag.mutateAsync({
							viewId: viewSelected?.ViewId ?? 0,
							tagId: viewTag.TagId,
							color: viewTag.Color,
							minScale: viewTag.MinScale,
							maxScale: viewTag.MaxScale,
							isVisible: viewTag.IsVisible,
							isAutoScale: viewTag.IsAutoScale,
							unit: viewTag.Unit,
							order: order,
							alias: viewTag.Alias,
							tagType: viewTag.TagType,
							sourceTagId: viewTag.SourceTagId,
							bitIndex: viewTag.BitIndex,
						}),
					),
			);

			const failed = results.filter((r) => r.status === "rejected");
			if (failed.length > 0) {
				setError("Some tags failed to save");
			} else {
				setSnackbarMessage("View saved successfully");
			}
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const updateViewTag = useMutation({
		mutationFn: ({
			viewId,
			tagId,
			color,
			minScale,
			maxScale,
			isVisible,
			isAutoScale,
			unit,
			order,
			alias,
			tagType,
			sourceTagId,
			bitIndex,
		}: {
			viewId: number;
			tagId: number;
			color: string;
			minScale: number;
			maxScale: number;
			isVisible: boolean;
			isAutoScale: boolean;
			unit: string;
			order?: number;
			alias?: string;
			tagType?: string;
			sourceTagId?: number;
			bitIndex?: number;
		}) =>
			upsertViewTag({
				viewId,
				tagId,
				color,
				minScale,
				maxScale,
				isVisible,
				isAutoScale,
				unit,
				order,
				alias,
				tagType,
				sourceTagId,
				bitIndex,
			}),
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const saveTags = useMutation({
		mutationFn: ({
			tagId,
			alias,
			color,
			minScale,
			maxScale,
			unit,
		}: {
			tagId: number;
			alias: string;
			color: string;
			minScale: number;
			maxScale: number;
			unit: string;
		}) => saveTagDefaults({ tagId, alias, color, minScale, maxScale, unit }),
		onSuccess: () => {
			setSnackbarMessage("Tag defaults saved successfully");
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	// Handler functions
	const handleDeleteTag = useCallback(
		(tagId: number) => {
			// Safety check: ensure tag exists
			if (!viewTags[tagId]) {
				setError(`Tag ${tagId} not found. The view may have changed.`);
				return;
			}

			// Remove tag from state - no refetch needed
			// The filteredSeries logic will handle reordering the cached series data
			const { [tagId]: removed, ...remainingTags } = viewTags;
			setViewTags(remainingTags);
		},
		[viewTags, setViewTags],
	);

	const handleAddTag = useCallback(
		(selectedTag: any) => {
			const newOrder =
				Math.max(...Object.values(viewTags).map((v) => v.order), 0) + 1;

			// Map TagTypeCode to TagType (A/D)
			const tagType =
				selectedTag.TagTypeCode === "A" || selectedTag.TagTypeCode === "D"
					? selectedTag.TagTypeCode
					: "A"; // Default to Analog if not specified

			const newViewTag = {
				TagId: selectedTag.TagId,
				TagName: selectedTag.TagName,
				TagType: tagType as "A" | "D",
				Alias: selectedTag.Alias || selectedTag.TagName,
				Color: selectedTag.Color || getRandomColor(),
				MinScale: selectedTag.MinScale ?? 0,
				MaxScale: selectedTag.MaxScale ?? 100,
				IsAutoScale: true,
				IsVisible: true,
				Unit: selectedTag.Unit || "",
			};

			const newViewTags = {
				...viewTags,
				[selectedTag.TagId]: {
					viewTag: newViewTag,
					order: newOrder,
				},
			};

			setViewTagsAndRefetch(newViewTags);
		},
		[viewTags, setViewTagsAndRefetch],
	);

	const handleTagSelect = (selectedTag: any) => {
		const existingTag = viewTags[selectedTag.TagId];

		// If tag already exists, show error
		if (existingTag) {
			setError(`${selectedTag.TagName} is already selected`);
			return;
		}

		// Add the new tag
		handleAddTag(selectedTag);
	};

	// Generate synthetic ID for bit-extracted tags
	const generateBitTagId = (sourceTagId: number, bitIndex: number): number => {
		return sourceTagId * 100 + bitIndex;
	};

	// Get set of existing bit tags as "sourceTagId-bitIndex" strings
	const existingBitTags = useMemo(() => {
		const bitTags = new Set<string>();
		Object.values(viewTags).forEach(({ viewTag }) => {
			if (
				viewTag.IsBitExtracted &&
				viewTag.SourceTagId !== undefined &&
				viewTag.BitIndex !== undefined
			) {
				bitTags.add(`${viewTag.SourceTagId}-${viewTag.BitIndex}`);
			}
		});
		return bitTags;
	}, [viewTags]);

	// Handle adding bits from a bit array tag
	const handleAddBits = useCallback(
		(sourceTagId: number, bitIndices: number[]) => {
			const sourceTagEntry = viewTags[sourceTagId];
			if (!sourceTagEntry) {
				setError("Source tag not found");
				return;
			}
			const sourceTag = sourceTagEntry.viewTag;

			const newTags: ViewTagsMap = { ...viewTags };
			let maxOrder = Math.max(
				...Object.values(viewTags).map((v) => v.order),
				0,
			);

			bitIndices.forEach((bitIndex) => {
				// Generate unique ID for this bit extraction
				const syntheticId = generateBitTagId(sourceTagId, bitIndex);

				// Skip if already exists
				if (newTags[syntheticId]) return;

				maxOrder++;
				newTags[syntheticId] = {
					viewTag: {
						TagId: syntheticId,
						TagName: `${sourceTag.TagName}[${bitIndex}]`,
						Alias: `${sourceTag.Alias || sourceTag.TagName}[${bitIndex}]`,
						TagType: "D", // Bit-extracted tags are always digital
						Color: getRandomColor(),
						MinScale: 0,
						MaxScale: 1,
						IsAutoScale: true,
						IsVisible: true,
						Unit: "",
						SourceTagId: sourceTagId,
						BitIndex: bitIndex,
						IsBitExtracted: true,
					},
					order: maxOrder,
				};
			});

			// Use setViewTags since bit-extracted tags don't need new API calls
			// They derive data from existing source tags
			setViewTags(newTags);
		},
		[viewTags, setViewTags],
	);

	const handleRowReorder = useCallback(
		(draggedId: number, targetId: number) => {
			if (draggedId === targetId) return;

			// Get all tags sorted by current order
			const sortedTags = Object.entries(viewTags)
				.map(([tagId, data]) => ({
					tagId: Number(tagId),
					...data,
				}))
				.sort((a, b) => a.order - b.order);

			const draggedIndex = sortedTags.findIndex((t) => t.tagId === draggedId);
			const targetIndex = sortedTags.findIndex((t) => t.tagId === targetId);

			if (draggedIndex === -1 || targetIndex === -1) return;

			// Reorder the array
			const reordered = [...sortedTags];
			const [removed] = reordered.splice(draggedIndex, 1);
			reordered.splice(targetIndex, 0, removed);

			// Update order property for all tags
			const updatedViewTags: typeof viewTags = {};
			reordered.forEach((tag, index) => {
				updatedViewTags[tag.tagId] = {
					viewTag: tag.viewTag,
					order: index,
				};
			});

			// Update local state only - will be saved when user clicks "Save View"
			// Use setViewTags (not setViewTagsAndRefetch) to preserve order when time filters change
			setViewTags(updatedViewTags);
		},
		[viewTags, setViewTags],
	);

	const handleSaveView = async () => {
		if (!viewSelected) {
			setSaveAsViewModalOpen(true);
			return;
		} else {
			setIsLoading(true);
			if (viewSelected?.ViewId) {
				await updateView.mutate({
					viewId: viewSelected?.ViewId,
					viewName: viewSelected.ViewName,
					isPublic: viewSelected.IsPublic,
				});
			}
			setIsLoading(false);
		}
	};

	const handleSaveDefaultToTag = async (tagId: number) => {
		if (viewSelected && tagId !== null) {
			const {
				TagId,
				Alias,
				Color,
				MinScale,
				MaxScale,
				Unit,
				IsVisible,
				IsAutoScale,
			} = viewTags[tagId].viewTag;

			await saveTags.mutate({
				tagId: TagId,
				alias: Alias,
				color: Color,
				minScale: MinScale,
				maxScale: MaxScale,
				unit: Unit,
			});

			await updateViewTag.mutate({
				viewId: viewSelected?.ViewId,
				tagId: TagId,
				color: Color,
				minScale: MinScale,
				maxScale: MaxScale,
				isVisible: IsVisible,
				isAutoScale: IsAutoScale,
				unit: Unit,
			});
		}
	};

	const handleCloseSnackbar = (
		event?: React.SyntheticEvent | Event,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarMessage("");
	};

	// Menu options function - uses refs to always access latest state
	// This prevents closure issues since context menu is registered once on mount
	const getMenuOptions = (data: any) => {
		let options: MenuOptionType[] = [];

		// If data exists, it means we right-clicked on a row
		if (data) {
			options.push({
				key: "delete",
				name: "Remove Tag",
				onClick: () => {
					if (!isNil(data)) {
						// Use ref to get latest handleDeleteTag function
						handleDeleteTagRef.current(data.TagId);
						setSelectedRowTagId(null);
					}
				},
			});
			options.push({
				key: "saveDefault",
				name: "Save Default To Tag",
				onClick: () => {
					handleSaveDefaultToTag(data.TagId);
				},
			});
		}

		// Always show these general options
		options = options.concat([
			{
				key: "add",
				name: "Tags Tree",
				onClick: () => {
					setTagsTreeModalOpen(true);
				},
			},
			{
				key: "addBitFromArray",
				name: "Add Bit from Bit Array",
				onClick: () => {
					setBitSelectorModalOpen(true);
				},
			},
			{
				key: "saveView",
				name: "Save View",
				onClick: () => {
					handleSaveViewRef.current();
				},
			},
			{
				key: "saveViewAs",
				name: "Save View as",
				onClick: () => {
					setSaveAsViewModalOpen(true);
				},
			},
			{
				key: "loadView",
				name: "Load View",
				onClick: () => {
					setLoadViewOpen(true);
				},
			},
		]);

		return options;
	};

	// Register context menu configuration - only once on mount
	useEffect(() => {
		registerConfig({
			id: "chartContextV2",
			getOptions: getMenuOptions,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Handler for context menu on the table
	const handleContextMenu = useCallback(
		(e: React.MouseEvent) => {
			if (
				tagsTreeModalOpen ||
				bitSelectorModalOpen ||
				saveAsViewModalOpen ||
				loadViewOpen
			) {
				return;
			}

			e.preventDefault();

			// Find the closest row element
			const target = e.target as HTMLElement;
			const rowElement = target.closest("[data-id]") as HTMLElement;

			if (rowElement) {
				// Right-clicked on a row - pass the viewTag data
				const rowId = rowElement.getAttribute("data-id");
				if (rowId) {
					const tagId = Number(rowId);
					// Use ref to get latest viewTags state
					const currentViewTags = viewTagsRef.current;
					const viewTag = currentViewTags[tagId]?.viewTag;
					if (viewTag) {
						setSelectedRowTagId(tagId);
						showContextMenu(e, viewTag, "chartContextV2");
						return;
					}
				}
			}

			// Right-clicked on empty table area - pass null to show only general options
			showContextMenu(e, null, "chartContextV2");
		},
		[
			showContextMenu,
			tagsTreeModalOpen,
			bitSelectorModalOpen,
			saveAsViewModalOpen,
			loadViewOpen,
		],
	);

	// Calculate cursor data values for each tag
	const cursorValues = useMemo(() => {
		if (!cursorData) return {};

		const values: Record<
			number,
			{
				y1: number;
				y2: number;
				y2y1: number;
				avg: number;
				min: number;
				max: number;
			}
		> = {};

		for (const tagId in cursorData.tagValues) {
			const data = cursorData.tagValues[tagId];
			values[tagId] = {
				y1: data.y1 || 0,
				y2: data.y2 || 0,
				y2y1: data.y2y1 || 0,
				avg: data.avg || 0,
				min: data.pmin?.y || 0,
				max: data.pmax?.y || 0,
			};
		}

		return values;
	}, [cursorData]);

	// Transform viewTags to DataGrid rows
	const rows: TagTableRow[] = useMemo(() => {
		return Object.values(viewTags)
			.filter((tag) => tag && tag.viewTag) // Safety: filter out invalid tags
			.sort((a, b) => b.order - a.order)
			.map(({ viewTag }) => {
				const cursorVal = cursorValues[viewTag.TagId] || {
					y1: 0,
					y2: 0,
					y2y1: 0,
					avg: 0,
					min: 0,
					max: 0,
				};

				const overallMinMax = seriesMinMax[viewTag.TagId] || {
					min: 0,
					max: 0,
				};

				return {
					id: viewTag.TagId,
					visible: viewTag.IsVisible,
					type: viewTag.TagType,
					alias: viewTag.Alias ? viewTag.Alias : viewTag.TagName,
					tagName: viewTag.TagName,
					color: viewTag.Color,
					minScale: viewTag.MinScale,
					maxScale: viewTag.MaxScale,
					autoScale: viewTag.IsAutoScale,
					unit: viewTag.Unit,
					deltaTime: cursorData ? cursorData.x2 - cursorData.x1 : 0,
					y1: cursorVal.y1,
					y2: cursorVal.y2,
					y2y1: cursorVal.y2y1,
					avg: cursorVal.avg,
					min: cursorVal.min,
					max: cursorVal.max,
					overallMin: overallMinMax.min,
					overallMax: overallMinMax.max,
					count: seriesPointCounts[viewTag.TagId] ?? 0,
					// Bit extraction metadata
					isBitExtracted: viewTag.IsBitExtracted,
					sourceTagId: viewTag.SourceTagId,
					bitIndex: viewTag.BitIndex,
				};
			});
	}, [viewTags, cursorValues, cursorData, seriesMinMax, seriesPointCounts]);

	// Handle cell edits
	const handleProcessRowUpdate = useCallback(
		(newRow: TagTableRow, oldRow: TagTableRow) => {
			const tagId = newRow.id;

			// Check if minScale or maxScale changed
			const minChanged = newRow.minScale !== oldRow.minScale;
			const maxChanged = newRow.maxScale !== oldRow.maxScale;

			// If min or max changed, automatically disable auto scale
			const shouldDisableAutoScale = minChanged || maxChanged;

			setViewTags({
				...viewTags,
				[tagId]: {
					...viewTags[tagId],
					viewTag: {
						...viewTags[tagId].viewTag,
						Alias: newRow.alias,
						MinScale: newRow.minScale,
						MaxScale: newRow.maxScale,
						IsAutoScale: shouldDisableAutoScale ? false : newRow.autoScale,
						Unit: newRow.unit,
						IsVisible: newRow.visible,
					},
				},
			});

			// Update the row to reflect auto scale being disabled
			if (shouldDisableAutoScale) {
				newRow.autoScale = false;
			}

			return newRow;
		},
		[viewTags, setViewTags],
	);

	// Custom cell renderer for type (with colored badge)
	const renderTypeCell = useCallback((params: GridRenderCellParams) => {
		const type = params.value as "A" | "D";
		const isBitExtracted = params.row.isBitExtracted;
		// Show "B" for bit-extracted tags, otherwise show the tag type
		const displayLabel = isBitExtracted ? "B" : type;
		// Use green for bit-extracted, purple for analog, orange for digital
		const backgroundColor = isBitExtracted
			? "#48C4A0"
			: type === "A"
				? "#AD48C4"
				: "#C46F48";
		return (
			<Chip
				label={displayLabel}
				size="small"
				style={{
					backgroundColor,
					color: "white",
					fontWeight: "bold",
					fontSize: "0.7rem",
					height: "20px",
				}}
			/>
		);
	}, []);

	// Use refs to always get current state in context menu callbacks
	// This prevents closure issues with the context menu registration
	const viewTagsRef = React.useRef(viewTags);
	const handleDeleteTagRef = React.useRef(handleDeleteTag);
	const handleSaveViewRef = React.useRef(handleSaveView);

	useEffect(() => {
		viewTagsRef.current = viewTags;
	}, [viewTags]);

	useEffect(() => {
		handleDeleteTagRef.current = handleDeleteTag;
	}, [handleDeleteTag]);

	useEffect(() => {
		handleSaveViewRef.current = handleSaveView;
	}, [handleSaveView, viewSelected]);

	const handleColorChange = useCallback(
		(tagId: number, newColor: string) => {
			const currentViewTags = viewTagsRef.current;

			if (!currentViewTags[tagId]) {
				return;
			}

			setViewTags({
				...currentViewTags,
				[tagId]: {
					...currentViewTags[tagId],
					viewTag: {
						...currentViewTags[tagId].viewTag,
						Color: newColor,
					},
				},
			});
		},
		[setViewTags],
	);

	// Custom cell renderer for color (color picker)
	const renderColorCell = useCallback(
		(params: GridRenderCellParams) => {
			const tagId = Number(params.row.id);

			return (
				<ColorPickerV2
					color={params.value as string}
					onColorChange={(newColor) => handleColorChange(tagId, newColor)}
				/>
			);
		},
		[handleColorChange],
	);

	// Custom cell renderer for alias with tooltip
	const renderAliasCell = useCallback((params: GridRenderCellParams) => {
		return (
			<Tooltip title={params.row.tagName} placement="top" arrow>
				<div style={{ width: "100%", cursor: "pointer" }}>{params.value}</div>
			</Tooltip>
		);
	}, []);

	// Custom cell renderer for visible checkbox (single-click)
	const renderVisibleCheckbox = useCallback(
		(params: GridRenderCellParams) => {
			const tagId = Number(params.row.id);

			const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
				const currentViewTags = viewTagsRef.current;

				if (!currentViewTags[tagId]) {
					return;
				}

				setViewTags({
					...currentViewTags,
					[tagId]: {
						...currentViewTags[tagId],
						viewTag: {
							...currentViewTags[tagId].viewTag,
							IsVisible: event.target.checked,
						},
					},
				});
			};

			return (
				<Checkbox
					checked={params.value as boolean}
					onChange={handleChange}
					color="primary"
					size="small"
					style={{ cursor: "pointer" }}
				/>
			);
		},
		[setViewTags],
	);

	// Custom cell renderer for autoScale checkbox (single-click)
	const renderAutoScaleCheckbox = useCallback(
		(params: GridRenderCellParams) => {
			const tagId = Number(params.row.id);

			const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
				const currentViewTags = viewTagsRef.current;

				if (!currentViewTags[tagId]) {
					return;
				}

				setViewTags({
					...currentViewTags,
					[tagId]: {
						...currentViewTags[tagId],
						viewTag: {
							...currentViewTags[tagId].viewTag,
							IsAutoScale: event.target.checked,
						},
					},
				});
			};

			return (
				<Checkbox
					checked={params.value as boolean}
					onChange={handleChange}
					color="primary"
					size="small"
					style={{ cursor: "pointer" }}
				/>
			);
		},
		[setViewTags],
	);

	// Define columns
	const columns: GridColDef[] = useMemo(
		() => [
			{
				field: "addTag",
				headerName: "",
				width: 50,
				sortable: false,
				filterable: false,
				disableColumnMenu: true,
				renderHeader: () => (
					<Tooltip title="Add Tag" arrow>
						<IconButton
							size="small"
							onClick={() => setTagsTreeModalOpen(true)}
							color="primary"
							style={{ padding: "4px", cursor: "pointer" }}
						>
							<PlaylistAdd fontSize="small" style={{ cursor: "pointer" }} />
						</IconButton>
					</Tooltip>
				),
				renderCell: () => (
					<div
						style={{
							cursor: "grab",
							padding: "4px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<DragIndicator fontSize="small" style={{ color: "#666" }} />
					</div>
				),
			},
			{
				field: "visible",
				headerName: "Visible",
				width: 70,
				type: "boolean",
				editable: false,
				renderCell: renderVisibleCheckbox,
			},
			{
				field: "type",
				headerName: "Type",
				width: 60,
				renderCell: renderTypeCell,
			},
			{
				field: "alias",
				headerName: "Alias/TagName",
				width: 320,
				editable: true,
				renderCell: renderAliasCell,
			},
			{
				field: "color",
				headerName: "Color",
				width: 80,
				renderCell: renderColorCell,
				editable: false,
			},
			{
				field: "minScale",
				headerName: "Min Scale",
				width: 90,
				editable: true,
				valueFormatter: (params) => {
					if (params == null) return "";
					return numberFormatterNoDecimal.format(Number(params));
				},
				cellClassName: (params: GridCellParams) => {
					return params.row.autoScale ? "disabled-cell" : "";
				},
				preProcessEditCellProps: (params: any) => {
					// Prevent editing if autoScale is enabled
					if (params.row.autoScale) {
						return { ...params.props, error: true };
					}

					const value = String(params.props.value).trim();

					// Allow empty during editing
					if (value === "") {
						return params.props;
					}

					// Validate: only digits and optional single decimal point
					// Pattern: optional negative, digits, optional decimal point with digits
					const validPattern = /^-?\d+\.?\d*$|^-?\d*\.\d+$/;

					if (!validPattern.test(value)) {
						return { ...params.props, error: true };
					}

					// Ensure it can be parsed as a valid number
					const cleanValue = value.replace(/,/g, "");
					const numValue = Number.parseFloat(cleanValue);

					if (Number.isNaN(numValue)) {
						return { ...params.props, error: true };
					}

					return params.props;
				},
			},
			{
				field: "maxScale",
				headerName: "Max Scale",
				width: 90,
				editable: true,
				valueFormatter: (params) => {
					if (params == null) return "";
					return numberFormatterNoDecimal.format(Number(params));
				},
				cellClassName: (params: GridCellParams) => {
					return params.row.autoScale ? "disabled-cell" : "";
				},
				preProcessEditCellProps: (params: any) => {
					// Prevent editing if autoScale is enabled
					if (params.row.autoScale) {
						return { ...params.props, error: true };
					}

					const value = String(params.props.value).trim();

					// Allow empty during editing
					if (value === "") {
						return params.props;
					}

					// Validate: only digits and optional single decimal point
					// Pattern: optional negative, digits, optional decimal point with digits
					const validPattern = /^-?\d+\.?\d*$|^-?\d*\.\d+$/;

					if (!validPattern.test(value)) {
						return { ...params.props, error: true };
					}

					// Ensure it can be parsed as a valid number
					const cleanValue = value.replace(/,/g, "");
					const numValue = Number.parseFloat(cleanValue);

					if (Number.isNaN(numValue)) {
						return { ...params.props, error: true };
					}

					return params.props;
				},
			},
			{
				field: "autoScale",
				headerName: "Auto",
				width: 70,
				type: "boolean",
				editable: false,
				renderCell: renderAutoScaleCheckbox,
			},
			{ field: "unit", headerName: "Unit", width: 70, editable: true },
			{
				field: "deltaTime",
				headerName: "X2-X1",
				width: 100,
				type: "number",
				valueFormatter: (params) => {
					if (params == null) return "";
					return formatDeltaTime(Number(params));
				},
			},
			{
				field: "y1",
				headerName: "Y1",
				width: 80,
				type: "number",
				valueFormatter: (params) => {
					if (params == null) return "";
					return numberFormatterOneDecimal.format(Number(params));
				},
			},
			{
				field: "y2",
				headerName: "Y2",
				width: 80,
				type: "number",
				valueFormatter: (params) => {
					if (params == null) return "";
					return numberFormatterOneDecimal.format(Number(params));
				},
			},
			{
				field: "y2y1",
				headerName: "Y2-Y1",
				width: 80,
				type: "number",
				valueFormatter: (params) => {
					if (params == null) return "";
					return numberFormatterOneDecimal.format(Number(params));
				},
			},
			{
				field: "avg",
				headerName: "Avg",
				width: 80,
				type: "number",
				valueFormatter: (params) => {
					if (params == null) return "";
					return numberFormatterOneDecimal.format(Number(params));
				},
			},
			{
				field: "min",
				headerName: "Min",
				width: 80,
				type: "number",
				valueFormatter: (params) => {
					if (params == null) return "";
					return numberFormatterOneDecimal.format(Number(params));
				},
			},
			{
				field: "max",
				headerName: "Max",
				width: 80,
				type: "number",
				valueFormatter: (params) => {
					if (params == null) return "";
					return numberFormatterOneDecimal.format(Number(params));
				},
			},
			{
				field: "count",
				headerName: "Count",
				width: 80,
				type: "number",
				valueFormatter: (params) => {
					if (params == null) return "";
					return numberFormatterNoDecimal.format(Number(params));
				},
			},
			{
				field: "actions",
				headerName: "",
				width: 80,
				sortable: false,
				filterable: false,
				disableColumnMenu: true,
				renderCell: (params: GridRenderCellParams) => (
					<div style={{ display: "flex", gap: "2px", alignItems: "center" }}>
						<Tooltip title="Save Default To Tag" arrow>
							<IconButton
								size="small"
								onClick={() => handleSaveDefaultToTag(Number(params.row.id))}
								color="primary"
								style={{ padding: "4px", cursor: "pointer" }}
							>
								<Save fontSize="small" style={{ cursor: "pointer" }} />
							</IconButton>
						</Tooltip>
						<Tooltip title="Remove Tag" arrow>
							<IconButton
								size="small"
								onClick={() => handleDeleteTag(Number(params.row.id))}
								color="secondary"
								style={{ padding: "4px", cursor: "pointer" }}
							>
								<Delete fontSize="small" style={{ cursor: "pointer" }} />
							</IconButton>
						</Tooltip>
					</div>
				),
			},
		],
		[
			renderVisibleCheckbox,
			renderAutoScaleCheckbox,
			renderTypeCell,
			renderColorCell,
			renderAliasCell,
			handleDeleteTag,
			handleSaveDefaultToTag,
			handleRowReorder,
			draggedRowId,
			dragOverRowId,
			setDraggedRowId,
			setDragOverRowId,
			numberFormatterOneDecimal,
			numberFormatterNoDecimal,
		],
	);

	return (
		<Paper
			style={{ height: "100%", width: "100%" }}
			onContextMenu={handleContextMenu}
		>
			<DataGrid
				rows={rows}
				columns={columns}
				disableRowSelectionOnClick
				density="compact"
				processRowUpdate={handleProcessRowUpdate}
				onProcessRowUpdateError={(error) => {
					setError(`Error updating row: ${error.message}`);
				}}
				hideFooter
				getRowClassName={(params) => {
					if (draggedRowId === params.id) return "dragging-row";
					if (dragOverRowId === params.id) return "drag-over-row";
					return "";
				}}
				slotProps={{
					row: {
						draggable: true,
						onDragStart: (e: React.DragEvent<HTMLDivElement>) => {
							e.stopPropagation();
							const target = e.currentTarget;
							const rowId = target.getAttribute("data-id");
							if (rowId) {
								setDraggedRowId(Number(rowId));
								e.dataTransfer.effectAllowed = "move";
							}
						},
						onDragEnd: (e: React.DragEvent<HTMLDivElement>) => {
							e.stopPropagation();
							setDraggedRowId(null);
							setDragOverRowId(null);
						},
						onDragOver: (e: React.DragEvent<HTMLDivElement>) => {
							e.preventDefault();
							e.stopPropagation();
							e.dataTransfer.dropEffect = "move";
							const target = e.currentTarget;
							const rowId = target.getAttribute("data-id");
							if (rowId && Number(rowId) !== draggedRowId) {
								setDragOverRowId(Number(rowId));
							}
						},
						onDragLeave: (e: React.DragEvent<HTMLDivElement>) => {
							e.stopPropagation();
							setDragOverRowId(null);
						},
						onDrop: (e: React.DragEvent<HTMLDivElement>) => {
							e.preventDefault();
							e.stopPropagation();
							const target = e.currentTarget;
							const rowId = target.getAttribute("data-id");
							if (
								rowId &&
								draggedRowId !== null &&
								Number(rowId) !== draggedRowId
							) {
								handleRowReorder(draggedRowId, Number(rowId));
							}
							setDraggedRowId(null);
							setDragOverRowId(null);
						},
						onMouseEnter: (e: React.MouseEvent<HTMLDivElement>) => {
							const target = e.currentTarget;
							const rowId = target.getAttribute("data-id");
							if (rowId) {
								const row = rows.find((r) => r.id === Number(rowId));
								if (row) {
									highlightSeries(row.alias || row.tagName);
								}
							}
						},
						onMouseLeave: () => {
							highlightSeries(null);
						},
					},
				}}
				sx={{
					"& .MuiDataGrid-cell": {
						padding: "0 8px",
						fontSize: "0.75rem",
						display: "flex",
						alignItems: "center",
					},
					"& .MuiDataGrid-cell--editable": {
						cursor: "pointer",
					},
					"& .MuiDataGrid-cell--editing input": {
						fontSize: "0.75rem",
					},
					"& .MuiDataGrid-cell.disabled-cell": {
						opacity: 0.4,
						backgroundColor: "#f5f5f5",
						cursor: "not-allowed",
						pointerEvents: "none",
					},
					"& .MuiDataGrid-columnHeader": {
						padding: "0 8px",
						fontSize: "0.75rem",
						fontWeight: 600,
					},
					"& .MuiDataGrid-row": {
						minHeight: "32px !important",
						maxHeight: "32px !important",
						cursor: "grab",
						"&.dragging-row": {
							opacity: 0.5,
							cursor: "grabbing",
						},
						"&.drag-over-row": {
							backgroundColor: "#e3f2fd",
						},
					},
					"& .MuiDataGrid-columnHeaderTitle": {
						fontWeight: 600,
					},
					"& .MuiDataGrid-cellContent": {
						display: "flex",
						alignItems: "center",
					},
				}}
			/>

			{/* Modals */}
			<TagsTreeModalV2
				open={tagsTreeModalOpen}
				handleClose={() => setTagsTreeModalOpen(false)}
				onTagSelect={handleTagSelect}
			/>

			<BitSelectorModal
				open={bitSelectorModalOpen}
				handleClose={() => setBitSelectorModalOpen(false)}
				viewTags={viewTags}
				onAddBits={handleAddBits}
				existingBitTags={existingBitTags}
			/>

			<SaveUpdateDeleteViewModalV2
				open={saveAsViewModalOpen}
				mode="create"
				handleClose={(shouldUpdate) => {
					setSaveAsViewModalOpen(false);
					if (shouldUpdate) {
						queryClient.invalidateQueries({ queryKey: ["views-v2"] });
					}
				}}
			/>

			<LoadViewModalV2
				open={loadViewOpen}
				handleClose={() => setLoadViewOpen(false)}
			/>

			{/* Error Modal */}
			<ErrorModal error={error} onHide={() => setError("")} />

			{/* Snackbar */}
			<Snackbar
				open={!!snackbarMessage}
				autoHideDuration={3000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert onClose={handleCloseSnackbar} severity="success">
					{snackbarMessage}
				</Alert>
			</Snackbar>
		</Paper>
	);
};
